// src/api/lenderPortal/mortgageSummary.js
// Product summary APIs for Overview + Analytics (All / BTL / Invoice Finance).

import { api, extractMessage } from "../client";
import { API_BASE_URL } from "../../utils/constants";

const ALL_SUMMARY_PATH =
  `${API_BASE_URL}/api/method/dynesourcerapi.api.get_product_summary`;
const BTL_SUMMARY_PATH =
  `${API_BASE_URL}/api/method/dynesourcerapi.api.get_mortgage_product_summary`;
const IF_SUMMARY_PATH =
  `${API_BASE_URL}/api/method/dynesourcerapi.api.get_product_summary`;

/** Canonical product-line keys used by Overview / Analytics dropdowns. */
export const PRODUCT_SUMMARY_TYPES = {
  ALL: "all",
  BTL: "btl",
  IF: "if",
};

/**
 * Map UI dropdown values → canonical summary type.
 * Accepts short codes ("all"/"btl"/"if") and Overview labels ("All", "Buy-to-Let", …).
 */
export function resolveProductSummaryType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "all" || raw === "all products") return PRODUCT_SUMMARY_TYPES.ALL;
  if (
    raw === "btl" ||
    raw === "buy-to-let" ||
    raw === "buy to let" ||
    raw === "buy-to-let mortgage"
  ) {
    return PRODUCT_SUMMARY_TYPES.BTL;
  }
  if (
    raw === "if" ||
    raw === "invoice" ||
    raw === "invoice finance" ||
    raw === "invoice-finance"
  ) {
    return PRODUCT_SUMMARY_TYPES.IF;
  }
  return PRODUCT_SUMMARY_TYPES.ALL;
}

function unwrapSummaryPayload(response) {
  const data = response?.data ?? response;
  const peeled = extractMessage(data);
  if (peeled && typeof peeled === "object" && !Array.isArray(peeled)) {
    // One more unwrap if still nested under message
    if (peeled.message && typeof peeled.message === "object" && !Array.isArray(peeled.message)) {
      return peeled.message;
    }
    return peeled;
  }
  return {};
}

function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function weightedAverage(aValue, aWeight, bValue, bWeight) {
  const aw = Math.max(0, num(aWeight));
  const bw = Math.max(0, num(bWeight));
  const total = aw + bw;
  if (total <= 0) return 0;
  return (num(aValue) * aw + num(bValue) * bw) / total;
}

/**
 * Flatten get_product_summary (type=all) into the same shape BTL/IF endpoints return
 * so Overview/Analytics KPI cards can read one consistent object.
 */
export function normalizeProductSummary(raw, type = PRODUCT_SUMMARY_TYPES.ALL) {
  if (!raw || typeof raw !== "object") return null;

  // Flat BTL / IF responses already have total_products at the root.
  if (raw.total_products != null && !raw.combined) {
    return raw;
  }

  const combined = raw.combined || {};
  const btl = raw.summaries?.BTL || {};
  const iff = raw.summaries?.IF || {};
  const btlTotal = num(btl.total_products ?? combined.by_type?.BTL?.total_products);
  const ifTotal = num(iff.total_products ?? combined.by_type?.IF?.total_products);

  const avgDeal = weightedAverage(
    btl.average_deal_size?.value ?? btl.average_deal_size?.current,
    btlTotal,
    iff.average_deal_size?.value ?? iff.average_deal_size?.current,
    ifTotal
  );
  const conv = weightedAverage(
    btl.conversion_rate?.current,
    btlTotal,
    iff.conversion_rate?.current,
    ifTotal
  );

  const btlTop = Array.isArray(btl.lender_ranking?.top_10) ? btl.lender_ranking.top_10 : [];
  const ifTop = Array.isArray(iff.lender_ranking?.top_10) ? iff.lender_ranking.top_10 : [];

  return {
    type: "all",
    available_types: raw.available_types || ["BTL", "IF"],
    by_type: combined.by_type || {},
    total_products: num(
      combined.total_products,
      btlTotal + ifTotal
    ),
    active_products: num(
      combined.active_products,
      num(btl.active_products) + num(iff.active_products)
    ),
    products_trend: btl.products_trend || iff.products_trend || null,
    active_products_trend:
      btl.active_products_trend || iff.active_products_trend || null,
    average_deal_size: {
      value: avgDeal,
      trend: "N/A",
      direction: "none",
      current: avgDeal,
      previous: 0,
    },
    conversion_rate: {
      value: `${conv.toFixed(1)}%`,
      trend: "N/A",
      direction: "none",
      current: conv,
      previous: 0,
    },
    total_applications: {
      value:
        num(btl.total_applications?.value ?? btl.total_applications?.current) +
        num(iff.total_applications?.value ?? iff.total_applications?.current),
      trend: "N/A",
      direction: "none",
    },
    lender_ranking: {
      current_user:
        btl.lender_ranking?.current_user ||
        iff.lender_ranking?.current_user ||
        null,
      top_10: [...btlTop, ...ifTop].slice(0, 10),
    },
    product_type_distribution: {
      ...(btl.product_type_distribution || {}),
      ...(iff.product_type_distribution || {}),
    },
    lender_distribution: {
      ...(btl.lender_distribution || {}),
      ...(iff.lender_distribution || {}),
    },
    time_to_cash: btl.time_to_cash ?? iff.time_to_cash ?? "N/A",
    summaries: raw.summaries,
    _sourceType: type,
  };
}

/** GET /api/method/dynesourcerapi.api.get_product_summary?type=all */
export async function getAllProductSummary() {
  const response = await api.get(ALL_SUMMARY_PATH, {
    params: { type: "all" },
  });
  return normalizeProductSummary(unwrapSummaryPayload(response), PRODUCT_SUMMARY_TYPES.ALL);
}

/**
 * GET /api/method/dynesourcerapi.api.get_mortgage_product_summary?type=BTL
 * (Legacy `applicant` param still accepted for older callers.)
 */
export async function getMortgageProductSummary(applicantOrOptions) {
  const params = {};
  if (applicantOrOptions && typeof applicantOrOptions === "object") {
    if (applicantOrOptions.type) params.type = applicantOrOptions.type;
    if (applicantOrOptions.applicant) params.applicant = applicantOrOptions.applicant;
  } else if (typeof applicantOrOptions === "string" && applicantOrOptions) {
    params.applicant = applicantOrOptions;
  }
  if (!params.type) params.type = "BTL";

  const response = await api.get(BTL_SUMMARY_PATH, { params });
  return normalizeProductSummary(unwrapSummaryPayload(response), PRODUCT_SUMMARY_TYPES.BTL);
}

/** GET /api/method/dynesourcerapi.api.get_product_summary?type=IF */
export async function getInvoiceFinanceSummary() {
  const response = await api.get(IF_SUMMARY_PATH, {
    params: { type: "IF" },
  });
  return normalizeProductSummary(unwrapSummaryPayload(response), PRODUCT_SUMMARY_TYPES.IF);
}

/**
 * Fetch the summary for a product-line dropdown value and return a normalized object
 * with total_products, active_products, average_deal_size, conversion_rate, lender_ranking.
 */
export async function getProductSummaryByType(typeOrLabel) {
  const type = resolveProductSummaryType(typeOrLabel);
  if (type === PRODUCT_SUMMARY_TYPES.BTL) {
    return getMortgageProductSummary({ type: "BTL" });
  }
  if (type === PRODUCT_SUMMARY_TYPES.IF) {
    return getInvoiceFinanceSummary();
  }
  return getAllProductSummary();
}

export default getMortgageProductSummary;
